/**
 * web/src/screens/Settings/us.tsx — the Us section of Settings (card
 * MudceTexpbq1): handle, inbox address, anniversary, inbox mode, Task Assist
 * and the occasions — moved verbatim out of index.tsx. State and saves stay
 * in the screen and arrive as props.
 */
import type { Dispatch, SetStateAction } from 'react';
import { getCoupleCode } from '../../lib/api';
import { InboxAddress } from '../../components/InboxAddress';
import { Switch } from '../../ui/Switch';
import { DATE_YEAR, isCustomKey, isDate, MAX_CUSTOM, PRESET_LEADS } from './helpers';
import type { CoupleSettings, Holiday } from './types';

export function UsSection({
  handle, handleDraft, setHandleDraft, handleDirty, commitHandle,
  settings, setSettings, save, saving, setSaveError,
  patchHolidays, setLead, customCount, addCustom,
}: {
  handle: string | null;
  handleDraft: string | null;
  setHandleDraft: Dispatch<SetStateAction<string | null>>;
  handleDirty: { current: boolean };
  commitHandle: () => Promise<void>;
  settings: CoupleSettings | null;
  setSettings: Dispatch<SetStateAction<CoupleSettings | null>>;
  save: (which: 'profile' | 'joint', body: Record<string, unknown>) => Promise<void>;
  saving: string | null;
  setSaveError: Dispatch<SetStateAction<string | null>>;
  patchHolidays: (next: Holiday[]) => void;
  setLead: (index: number, raw: number) => void;
  customCount: number;
  addCustom: () => void;
}) {
  return (
    <>
        {/* ── Us: the shared half ────────────────────────────────────── */}
        <section className="set-section" aria-labelledby="set-us-title">
          <h3 className="set-section-title" id="set-us-title">
            Us
          </h3>

          {/* ── Shared handle (card w8Mj4ThRii6Z, accounts Phase E) — the
              couple's own name, minted at pair-creation and replaceable here.
              It doubles as a sign-in identifier, which is why the note says so. */}
          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Handle</div>
              <div className="set-note">
                Your couple's name — one you can also sign in with.
              </div>
            </div>
            {handle === null ? (
              <p className="set-loading" aria-busy="true">Loading…</p>
            ) : (
              <input
                type="text"
                id="set-handle"
                className="set-name-input"
                aria-label="Handle"
                maxLength={40}
                spellCheck={false}
                autoComplete="off"
                value={handleDraft ?? handle}
                onChange={e => {
                  handleDirty.current = true;
                  setHandleDraft(e.target.value);
                }}
                onBlur={() => void commitHandle()}
                onKeyDown={e => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
              />
            )}
          </div>

          {/* ── Shared inbox address (card qntf9Qa0Cemr) — where the couple
              emails so Pear can take it in. Built from the server-issued code,
              same value the Admin menu and invite moment show. */}
          <InboxAddress
            code={getCoupleCode()}
            idPrefix="settings"
            variant="row"
            note="Email bills, bookings and receipts here — the concierge sorts each one into your shared space."
          />

          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Anniversary</div>
              <div className="set-note">The date you two count from.</div>
            </div>
            <input
              type="date"
              id="set-anniversary"
              className="set-date"
              aria-label="Anniversary"
              value={settings?.anniversary ?? ''}
              onChange={e => {
                const value = e.target.value || null;
                if (value && !isDate(value)) return;
                if (settings) setSettings({ ...settings, anniversary: value });
                void save('joint', { anniversary: value });
              }}
            />
          </div>

          <div className="set-mode" role="group" aria-labelledby="set-mode-label">
            <div className="set-text">
              <div className="set-label" id="set-mode-label">From your inbox</div>
              <div className="set-note">
                When a bill or booking lands: ask us first, or file it as a to-do
                straight away. The to-do permission in Settings → Concierge is the off switch.
              </div>
            </div>
            <div className="set-mode-options">
              {(['suggest', 'auto'] as const).map(mode => (
                <button
                  type="button"
                  key={mode}
                  id={mode === 'suggest' ? 'set-mode-suggest' : 'set-mode-auto'}
                  role="radio"
                  aria-checked={settings?.inboxTodoMode === mode}
                  className={`set-mode-btn${settings?.inboxTodoMode === mode ? ' is-on' : ''}`}
                  disabled={saving === 'joint'}
                  onClick={() => {
                    if (!settings || settings.inboxTodoMode === mode) return;
                    setSettings({ ...settings, inboxTodoMode: mode });
                    void save('joint', { inboxTodoMode: mode });
                  }}
                >
                  {mode === 'suggest' ? 'Ask us first' : 'Add automatically'}
                </button>
              ))}
            </div>
          </div>

          {/* ── Task Assist (spec kZX3gsJkKaTH) — the couple-level consent
              switch for the chore-balance engine. One row, so both partners
              read the same state, and off until someone says yes. The
              one-time offer that points here lives in the Life rail; this is
              the standing door, findable without waiting to be offered. ── */}
          <div className="set-mode" role="group" aria-labelledby="set-task-assist-label">
            <div className="set-text">
              <div className="set-label" id="set-task-assist-label">Task Assist</div>
              <div className="set-note">
                If it looks like something is sliding, I’ll suggest an approach
                that may work better for everyone.
              </div>
            </div>
            <div className="set-mode-options">
              {([true, false] as const).map(on => (
                <button
                  type="button"
                  key={String(on)}
                  id={on ? 'set-task-assist-on' : 'set-task-assist-off'}
                  role="radio"
                  aria-checked={(settings?.taskAssist?.enabled ?? false) === on}
                  className={`set-mode-btn${(settings?.taskAssist?.enabled ?? false) === on ? ' is-on' : ''}`}
                  disabled={saving === 'joint'}
                  onClick={() => {
                    if (!settings) return;
                    if ((settings.taskAssist?.enabled ?? false) === on) return;
                    setSettings({ ...settings, taskAssist: { enabled: on, offer: false } });
                    void save('joint', { taskAssist: on });
                  }}
                >
                  {on ? 'On' : 'Off'}
                </button>
              ))}
            </div>
          </div>

          <div className="set-group">
            <div className="set-group-head">
              <span className="set-group-title">Occasions</span>
              <span className="set-group-note">
                How far ahead the concierge starts asking — off means no reminder for that one.
              </span>
            </div>

            {!settings && <p className="set-loading" aria-busy="true">Loading…</p>}

            {settings?.holidays.map((h, i) => {
              const custom = isCustomKey(h.key);
              const name = h.label || (custom ? 'custom occasion' : h.key);
              return (
                <div className="set-row set-holiday" key={h.key}>
                  <div className="set-text">
                    <div className="set-label">
                      {custom ? (
                        <input
                          type="text"
                          id={`set-custom-label-${i}`}
                          className="set-text-input set-custom-label"
                          placeholder="Name a custom occasion"
                          maxLength={60}
                          aria-label="Custom occasion name"
                          value={h.label}
                          onChange={e => {
                            const next = [...settings.holidays];
                            next[i] = { ...h, label: e.target.value };
                            patchHolidays(next);
                          }}
                        />
                      ) : (
                        h.label || h.key
                      )}
                    </div>

                    {custom && (
                      <div className="set-note set-custom-date">
                        <label className="set-custom-date-label" htmlFor={`set-custom-date-${i}`}>
                          Date
                        </label>
                        <input
                          type="date"
                          id={`set-custom-date-${i}`}
                          className="set-date set-custom-date-input"
                          aria-label={`${name} date — repeats every year`}
                          value={h.date ? `${DATE_YEAR}-${h.date}` : ''}
                          onChange={e => {
                            const picked = e.target.value;
                            const next = [...settings.holidays];
                            next[i] = { ...h, date: picked ? picked.slice(5) : null };
                            patchHolidays(next);
                          }}
                        />
                        <span className="set-custom-date-note">Repeats every year</span>
                      </div>
                    )}

                    <div className="set-note set-lead">
                      <span>Remind us</span>
                      <div className="set-stepper">
                        <button
                          type="button"
                          className="set-stepper-btn"
                          aria-label={`${name} lead time minus`}
                          disabled={h.leadDays <= 1}
                          onClick={() => setLead(i, h.leadDays - 1)}
                        >
                          &minus;
                        </button>
                        <input
                          type="number"
                          className="set-lead-input"
                          aria-label={`${name} lead time in days`}
                          min={1}
                          max={365}
                          value={h.leadDays}
                          onChange={e => {
                            const raw = Number(e.target.value);
                            if (!Number.isFinite(raw)) return;
                            setLead(i, raw);
                          }}
                        />
                        <button
                          type="button"
                          className="set-stepper-btn"
                          aria-label={`${name} lead time plus`}
                          disabled={h.leadDays >= 365}
                          onClick={() => setLead(i, h.leadDays + 1)}
                        >
                          +
                        </button>
                      </div>
                      <span>days ahead</span>
                      <div className="set-presets" role="group"
                        aria-label={`${name} lead time presets`}>
                        {PRESET_LEADS.map(d => (
                          <button
                            key={d}
                            type="button"
                            className={`set-preset-chip${h.leadDays === d ? ' is-on' : ''}`}
                            aria-pressed={h.leadDays === d}
                            onClick={() => setLead(i, d)}
                          >
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="set-row-side">
                    <Switch
                      checked={h.enabled}
                      label={`${name} reminder`}
                      disabled={saving === 'joint'}
                      onChange={() => {
                        // The DO refuses to arm a custom occasion with no name or
                        // no date — say so here instead of PUTting a guaranteed 400.
                        if (custom && !h.enabled && (!h.label.trim() || !h.date)) {
                          const needsDate = !h.date;
                          setSaveError(needsDate ? 'Give it a date first.' : 'Give it a name first.');
                          document
                            .getElementById(needsDate ? `set-custom-date-${i}` : `set-custom-label-${i}`)
                            ?.focus();
                          return;
                        }
                        const next = [...settings.holidays];
                        next[i] = { ...h, enabled: !h.enabled };
                        patchHolidays(next);
                      }}
                    />
                    {custom && (
                      <button
                        type="button"
                        className="set-remove"
                        id={`set-remove-${i}`}
                        disabled={saving === 'joint'}
                        aria-label={`Remove ${name}`}
                        onClick={() => patchHolidays(settings.holidays.filter((_, j) => j !== i))}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {settings && customCount < MAX_CUSTOM && (
              <button
                type="button"
                className="set-add-occasion"
                id="btn-add-occasion"
                disabled={saving === 'joint'}
                onClick={addCustom}
              >
                Add another occasion
              </button>
            )}
          </div>
        </section>
    </>
  );
}
